import React, { useState, useEffect, useRef, useCallback } from 'react';
import { loadImages } from './imageLoader';
// Import all our organized components
import { drawSpecky } from './components/Ghost';
import { drawAllPlatforms } from './components/Platform';
import { drawAllEnemies } from './components/Enemy';
import { drawAllCoins } from './components/Coin';
import { drawAllBoosts } from './components/Boost';
import { drawAllProjectiles } from './components/Projectile';

// Import game logic
import { createInitialGameState, resetGameState } from './game/gameState';
import { stepSimulation, TICKS } from './game/simulation';
import { drawParticles, triggerShake, spawnBurst } from './game/particles';
import { initAudio, setAudioSettings, sfx } from './game/audio';
import { computeScore } from './game/score';
import { useWallet } from './chain/useWallet';
import { initTelegram, getTelegramUser, haptic } from './telegram/telegram';

// Display helpers for the wallet bar
const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
const fmtAmount = (n) => (n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

// True on phones/tablets — picks which control hint to show on the first run.
const isTouchDevice =
  typeof window !== 'undefined' &&
  ('ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0);

const SpeckyJump = () => {
  const canvasRef = useRef(null);
  const wallet = useWallet();
  const [score, setScore] = useState(0); // live height in metres
  const [liveScore, setLiveScore] = useState(0); // live composite total score
  const [finalStats, setFinalStats] = useState(null); // score breakdown at game over
  const [highScore, setHighScore] = useState(() => {
    const saved = Number(localStorage.getItem('speckyBestScore'));
    return Number.isFinite(saved) ? saved : 0;
  });
  const [gameOver, setGameOver] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [coins, setCoins] = useState(0);
  const [coinsTotal, setCoinsTotal] = useState(0);
  const [enemiesKilled, setEnemiesKilled] = useState(0);
  const [activeBoost, setActiveBoost] = useState(null);
  const [boostTimer, setBoostTimer] = useState(0);
  const [ammo, setAmmo] = useState(10);
  const [rocketProgress, setRocketProgress] = useState(0);
  const [shieldSecs, setShieldSecs] = useState(0);
  const [options, setOptions] = useState({
    enemiesEnabled: true,
    boostsEnabled: true,
    soundEnabled: true,
    tiltEnabled: false
  });
  const [tiltStatus, setTiltStatus] = useState(null); // null | 'ok' | 'insecure' | 'denied' | 'unsupported'
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [showHint, setShowHint] = useState(false); // first-run control hint
  const [devMode, setDevMode] = useState(false);
  const [tgUser, setTgUser] = useState(null); // Telegram Mini App user, if any
  const gameStateRef = useRef(null);
  if (gameStateRef.current === null) {
    gameStateRef.current = createInitialGameState();
  }
  // Tracks the last value pushed to each HUD state so we only re-render on change
  const hudRef = useRef({});
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const imagesRef = useRef(null);
  const [paused, setPaused] = useState(false);
  const pausedRef = useRef(false); // read by the game loop without restarting it

  // Pause/resume. Timers are tick-based and the loop stops advancing ticks while
  // paused, so they freeze automatically — no timestamp shifting needed.
  const togglePause = useCallback(() => {
    const game = gameStateRef.current;
    const willPause = !pausedRef.current;
    pausedRef.current = willPause;
    setPaused(willPause);

    if (willPause) {
      sfx.rocketStop(); // silence the sustained whoosh while paused
    } else {
      game.keys = {}; // clear any keys held down during pause
      // The game loop restarts the rocket whoosh on resume if still active.
    }
  }, []);

  // Record a finished run's score, persisting a new best to localStorage.
  // Uses a functional update so it never reads a stale highScore from a closure.
  const recordScore = useCallback((finalScore) => {
    setHighScore((prev) => {
      if (finalScore > prev) {
        try {
          localStorage.setItem('speckyBestScore', String(finalScore));
        } catch (e) {
          // localStorage may be unavailable (private mode); high score stays in-memory
        }
        haptic('success'); // celebratory buzz on a new best (Telegram only)
        return finalScore;
      }
      return prev;
    });
  }, []);

  // Ask for motion access (iOS requires a user gesture + HTTPS). Returns whether
  // tilt steering can actually be turned on, and records why if it can't.
  const requestTilt = useCallback(async () => {
    if (!window.isSecureContext) { setTiltStatus('insecure'); return false; }
    const DOE = window.DeviceOrientationEvent;
    if (typeof DOE === 'undefined') { setTiltStatus('unsupported'); return false; }
    if (typeof DOE.requestPermission === 'function') {
      try {
        const res = await DOE.requestPermission();
        if (res !== 'granted') { setTiltStatus('denied'); return false; }
      } catch {
        setTiltStatus('denied'); return false;
      }
    }
    setTiltStatus('ok');
    return true;
  }, []);

  const toggleTilt = useCallback(async () => {
    const game = gameStateRef.current;
    const turningOn = !game.options.tiltEnabled;
    if (turningOn) {
      const ok = await requestTilt();
      if (!ok) return; // leave the toggle off; the status message explains why
    } else {
      setTiltStatus(null);
    }
    game.options.tiltEnabled = turningOn;
    game.keys = {};
    game.player.vx = 0;
    game.tiltHasBaseline = false; // recalibrate neutral next time it's enabled
    game.tiltActive = false;
    setOptions((prev) => ({ ...prev, tiltEnabled: turningOn }));
  }, [requestTilt]);

  // Feed smoothed left-right tilt into the game while tilt steering is on.
  useEffect(() => {
    if (!options.tiltEnabled) return;
    const game = gameStateRef.current;
    const onOrient = (e) => {
      if (e.gamma == null) return;
      if (!game.tiltHasBaseline) {
        // First reading of the run becomes "centre" — automatic calibration.
        game.tiltBaseline = e.gamma;
        game.tiltGamma = e.gamma;
        game.tiltHasBaseline = true;
        game.tiltActive = true;
        return;
      }
      // Light low-pass: enough to kill hand-shake jitter, but tracks the tilt
      // quickly so steering feels responsive.
      game.tiltGamma = game.tiltGamma * 0.6 + e.gamma * 0.4;
      game.tiltActive = true;
      // Continuous auto-centring: while the phone is held near neutral, drift the
      // baseline toward the current angle. The player naturally passes through
      // centre constantly, so "neutral" self-corrects and never needs calibrating
      // by hand. The drift only happens near centre, so it never fights a
      // deliberate tilt.
      const RECENTER_ZONE = 5;   // deg from baseline still treated as "resting"
      const RECENTER_RATE = 0.05;
      if (Math.abs(game.tiltGamma - game.tiltBaseline) < RECENTER_ZONE) {
        game.tiltBaseline += (game.tiltGamma - game.tiltBaseline) * RECENTER_RATE;
      }
    };
    window.addEventListener('deviceorientation', onOrient, true);
    return () => {
      window.removeEventListener('deviceorientation', onOrient, true);
      game.tiltActive = false;
    };
  }, [options.tiltEnabled]);

  useEffect(() => {
    loadImages().then((images) => {
      imagesRef.current = images;
      window.gameImages = images; // Make available globally
      setImagesLoaded(true);
    });
  }, []);

  // Bring up the Telegram Mini App shell (no-op in a plain browser).
  useEffect(() => {
    initTelegram();
    setTgUser(getTelegramUser());
  }, []);

  // Toggle dev mode with 'D' while on the menu (the in-game loop isn't running here)
  useEffect(() => {
    if (gameStarted) return;
    const onKeyDown = (e) => {
      if (e.key === 'D') {
        setDevMode((prev) => !prev);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [gameStarted]);

  useEffect(() => {
    if (!gameStarted || gameOver) return;
    
    const cvs = canvasRef.current;
    if (!cvs) return;
    
    const ctx = cvs.getContext('2d');
    const game = gameStateRef.current;
    
    let animationFrameId = null;
    let isRunning = true;

    // Boost activation itself lives in the shared simulation (activateBoostState).
    // Here we only play the per-pickup sound, driven by the events the sim emits.
    // The rocket's sustained whoosh is handled in the loop while rocketActive.
    const playBoostSfx = (type) => {
      if (type === 'magnet') sfx.magnet();
      else if (type === 'shield') sfx.shield();
      else if (type === 'slowmo') sfx.slowmo();
      else if (type !== 'rocket') sfx.powerup();
    };

    // Record an input event, tagged with the current simulation tick, so a run
    // can be replayed/validated later from { seed, inputLog }. Capped so a very
    // long run can't grow the log without bound.
    const MAX_INPUTS = 12000;
    const logInput = (a) => {
      if (game.inputLog.length < MAX_INPUTS) {
        game.inputLog.push({ t: game.animationFrame, a });
      }
    };

    const isLeftKey = (k) => k === 'ArrowLeft' || k === 'a' || k === 'A';
    const isRightKey = (k) => k === 'ArrowRight' || k === 'd' || k === 'D';
    const isShootKey = (k) => k === 'ArrowUp' || k === 'w' || k === 'W';

    // Hide the first-run control hint as soon as the player does anything.
    const dismissHint = () => setShowHint(false);

    // Assemble a self-contained, replayable record of the finished run. This is
    // what a future backend validator (Phase 4) will re-simulate to confirm the
    // score before it counts toward the on-chain weekly leaderboard.
    const RUN_SCHEMA = 1;
    const finalizeRun = (fs) => {
      game.lastRunReceipt = {
        v: RUN_SCHEMA,
        seed: game.seed,
        durationTicks: game.animationFrame,
        stats: fs,
        inputs: game.inputLog,
      };
    };

    // Inputs only set canonical held-key flags / a shoot pulse. The simulation
    // reads them each tick and decides movement + firing, so play replays exactly.
    const handleKeyDown = (e) => {
      // Escape / P toggles pause; ignore all other input while paused.
      if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
        togglePause();
        return;
      }
      if (pausedRef.current) return;

      if (isLeftKey(e.key)) {
        if (!game.keys['__left']) { game.keys['__left'] = true; logInput('L1'); }
        dismissHint();
      } else if (isRightKey(e.key)) {
        if (!game.keys['__right']) { game.keys['__right'] = true; logInput('R1'); }
        dismissHint();
      } else if (isShootKey(e.key)) {
        if (!game.keys['__shoot']) { game.keys['__shoot'] = true; logInput('SH1'); }
        dismissHint();
      }
    };

    const handleKeyUp = (e) => {
      if (isLeftKey(e.key)) { game.keys['__left'] = false; logInput('L0'); }
      else if (isRightKey(e.key)) { game.keys['__right'] = false; logInput('R0'); }
      else if (isShootKey(e.key)) { game.keys['__shoot'] = false; logInput('SH0'); }
    };

    // Multi-touch slide model: each finger is tracked by id. A finger that slides
    // past the slop becomes the steering finger and moves Specky *relatively* — by
    // how far it slides, not where it is — so lifting and re-touching elsewhere
    // never jumps the character, and you can slide-lift-slide to keep crossing the
    // screen. Any finger that goes down and lifts without sliding (a tap) fires a
    // shot, so you can slide with one hand and tap-shoot with the other at once.
    const TAP_SLOP = 10; // px of movement still counted as a tap, not a slide
    const touchPts = new Map(); // id -> { startX, startY, x, lastX, moved }
    let moveTouchId = null;

    // CSS px -> canvas px, so a slide maps the same on any screen size.
    const canvasScaleX = () => {
      const rect = cvs.getBoundingClientRect();
      return rect.width ? cvs.width / rect.width : 1;
    };

    // After the steering finger lifts, hand steering to another sliding finger if
    // one is still down (re-anchored, so no jump); otherwise stop moving.
    const reassignMoveTouch = () => {
      moveTouchId = null;
      for (const [id, rec] of touchPts) {
        if (rec.moved) { moveTouchId = id; rec.lastX = rec.x; game.isTouching = true; return; }
      }
      game.isTouching = false;
      game.touchDragDX = 0;
      game.player.vx = 0;
    };

    const handleTouchStart = (e) => {
      if (pausedRef.current) return;
      dismissHint();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i];
        touchPts.set(t.identifier, {
          startX: t.clientX, startY: t.clientY, x: t.clientX, lastX: t.clientX, moved: false,
        });
      }
      // Tilt mode: the phone steers, so any finger down means "hold to shoot".
      if (game.options.tiltEnabled && !game.keys['__shoot']) {
        game.keys['__shoot'] = true; logInput('SH1');
      }
      e.preventDefault();
    };

    const handleTouchMove = (e) => {
      // Tilt mode steers from orientation, so touches never drive movement.
      if (game.options.tiltEnabled) { e.preventDefault(); return; }
      const scale = canvasScaleX();
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i];
        const rec = touchPts.get(t.identifier);
        if (!rec) continue;
        rec.x = t.clientX;
        if (!rec.moved &&
            (Math.abs(t.clientX - rec.startX) > TAP_SLOP || Math.abs(t.clientY - rec.startY) > TAP_SLOP)) {
          rec.moved = true;
          moveTouchId = t.identifier;  // this finger now owns steering
          rec.lastX = t.clientX;       // anchor here so the slop isn't a jump
        }
        if (t.identifier === moveTouchId) {
          game.touchDragDX += (t.clientX - rec.lastX) * scale; // relative slide
          rec.lastX = t.clientX;
          game.isTouching = true;
        }
      }
      e.preventDefault();
    };

    // Shared end/cancel handler. `fire` is false for touchcancel (no shot).
    const endTouches = (e, fire) => {
      for (let i = 0; i < e.changedTouches.length; i++) {
        const t = e.changedTouches[i];
        const rec = touchPts.get(t.identifier);
        if (!rec) continue;
        touchPts.delete(t.identifier);
        if (game.options.tiltEnabled) continue;
        if (fire && !rec.moved) {
          game.shootPulse = true; // a tap (no slide) fires, even while sliding
          logInput('S');
        }
        if (t.identifier === moveTouchId) reassignMoveTouch();
      }
      // Tilt mode: auto-fire stops once every finger is off the screen.
      if (game.options.tiltEnabled && touchPts.size === 0 && game.keys['__shoot']) {
        game.keys['__shoot'] = false; logInput('SH0');
      }
    };

    const handleTouchEnd = (e) => endTouches(e, true);
    const handleTouchCancel = (e) => endTouches(e, false);

    const finalizeAndEnd = () => {
      const fs = computeScore(game);
      finalizeRun(fs);
      setFinalStats(fs);
      setGameOver(true);
      recordScore(fs.total);
    };

    const update = () => {
      if (gameOver || !isRunning) return;

      // The run has ended in the simulation. Enemy deaths get a brief on-screen
      // freeze (red flash + shake) before the game-over card; falls end at once.
      if (game.gameOver) {
        if (game.gameOverReason === 'enemy' && (game.animationFrame - game.deathEndTick) < TICKS.death) {
          game.animationFrame++; // advance the freeze timer
          return;
        }
        finalizeAndEnd();
        return;
      }

      // One deterministic simulation tick (shared with the validator).
      stepSimulation(game, cvs.width, cvs.height);

      // React to the side-effects the sim emitted this tick.
      for (const ev of game.events) {
        if (ev.k === 'boost') {
          playBoostSfx(ev.type);
          haptic('light');
        } else if (ev.k === 'dead') {
          game.deathEndTick = game.animationFrame;
          sfx.gameover();
          haptic('heavy');
          if (ev.reason === 'enemy') {
            triggerShake(game, 10, 22);
            spawnBurst(game, game.player.x + game.player.w / 2, game.player.y + game.player.h / 2, '#ff4d4d', 20, 6);
          }
        }
      }

      // Sustained rocket whoosh for the full boost duration.
      if (game.rocketActive) {
        sfx.rocketStart();
      } else {
        sfx.rocketStop();
      }

      syncHud();
    }; // End of update function

    // Push game state to React HUD only when a displayed value actually changes,
    // so we avoid re-rendering the component ~60 times per second.
    const syncHud = () => {
      const h = hudRef.current;
      const tick = game.animationFrame;

      if (game.score !== h.score) { h.score = game.score; setScore(game.score); }
      const live = computeScore(game).total;
      if (live !== h.liveScore) { h.liveScore = live; setLiveScore(live); }
      if (game.coinsCollectedCount !== h.coins) { h.coins = game.coinsCollectedCount; setCoins(game.coinsCollectedCount); }
      if (game.coinsSpawnedTotal !== h.coinsTotal) { h.coinsTotal = game.coinsSpawnedTotal; setCoinsTotal(game.coinsSpawnedTotal); }
      if (game.enemiesKilled !== h.enemiesKilled) { h.enemiesKilled = game.enemiesKilled; setEnemiesKilled(game.enemiesKilled); }
      if (game.coinsForRocket !== h.rocketProgress) { h.rocketProgress = game.coinsForRocket; setRocketProgress(game.coinsForRocket); }
      if (game.bulletsRemaining !== h.ammo) { h.ammo = game.bulletsRemaining; setAmmo(game.bulletsRemaining); }

      const ab = game.activeBoost || null;
      if (ab !== h.activeBoost) { h.activeBoost = ab; setActiveBoost(ab); }
      const bt = ab ? Math.max(0, Math.ceil((game.boostEndTick - tick) / 60)) : 0;
      if (bt !== h.boostTimer) { h.boostTimer = bt; setBoostTimer(bt); }

      const ss = game.shieldActive ? Math.max(0, Math.ceil((game.shieldEndTick - tick) / 60)) : 0;
      if (ss !== h.shieldSecs) { h.shieldSecs = ss; setShieldSecs(ss); }
    };

    // Gradients depend only on the (fixed) canvas size, so build them once and reuse
    // each frame instead of recreating 4 gradient objects ~60 times per second.
    const bgGradient = ctx.createLinearGradient(0, 0, 0, cvs.height);
    bgGradient.addColorStop(0, '#0a0e27');
    bgGradient.addColorStop(0.5, '#16213e');
    bgGradient.addColorStop(1, '#1a1a2e');

    // Brand-tinted nebulas: Phantasma cyan, phoenix red, and a deep blue.
    const nebulaGradients = [
      { x: cvs.width * 0.3, y: cvs.height * 0.2, color: 'rgba(0, 212, 255, 0.18)', size: 190 },
      { x: cvs.width * 0.7, y: cvs.height * 0.6, color: 'rgba(255, 77, 94, 0.14)', size: 220 },
      { x: cvs.width * 0.5, y: cvs.height * 0.82, color: 'rgba(43, 123, 255, 0.16)', size: 240 }
    ].map(nebula => {
      const g = ctx.createRadialGradient(nebula.x, nebula.y, 0, nebula.x, nebula.y, nebula.size);
      g.addColorStop(0, nebula.color);
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      return g;
    });

    const drawBackground = () => {
  ctx.fillStyle = bgGradient;
  ctx.fillRect(0, 0, cvs.width, cvs.height);

  nebulaGradients.forEach(g => {
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cvs.width, cvs.height);
  });

  // Draw stars with varying brightness
  game.stars.forEach(star => {
    const twinkle = Math.sin(Date.now() * 0.001 + star.x) * 0.3 + 0.7;
    
    // Make some stars extra shiny
    const isShiny = star.brightness > 0.8;
    
    ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness * twinkle})`;
    ctx.beginPath();
    ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
    ctx.fill();
    
    // Add glow for bright stars
    if (isShiny || star.size > 1.5) {
      ctx.fillStyle = `rgba(200, 220, 255, ${star.brightness * twinkle * 0.15})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size * 1.5, 0, Math.PI * 2);
      ctx.fill();
      
      // Extra sparkle for very bright stars
      if (isShiny) {
        ctx.fillStyle = `rgba(255, 255, 255, ${star.brightness * twinkle * 0.25})`;
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size * 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  });
};

    const drawPlayer = () => {
      const p = game.player;
      
      if (game.hitEnemy) {
        ctx.fillStyle = 'rgba(255, 0, 0, 0.3)';
        ctx.fillRect(0, 0, cvs.width, cvs.height);
      }
      
      if (game.rocketActive) {
        ctx.fillStyle = '#FF6B00';
        ctx.beginPath();
        ctx.moveTo(p.x + p.w/2 - 10, p.y + p.h);
        ctx.lineTo(p.x + p.w/2, p.y + p.h + 20);
        ctx.lineTo(p.x + p.w/2 + 10, p.y + p.h);
        ctx.fill();
        
        ctx.fillStyle = '#FFD700';
        ctx.beginPath();
        ctx.moveTo(p.x + p.w/2 - 6, p.y + p.h);
        ctx.lineTo(p.x + p.w/2, p.y + p.h + 15);
        ctx.lineTo(p.x + p.w/2 + 6, p.y + p.h);
        ctx.fill();
      }
      
      drawSpecky(ctx, p.x, p.y + p.floatOffset, p.w, p.h, game.animationFrame, imagesRef.current?.specky, p);
      
      if (game.shieldActive) {
        const shieldPulse = Math.sin(Date.now() * 0.005) * 0.2 + 0.8;
        ctx.strokeStyle = `rgba(78, 205, 196, ${shieldPulse})`;
        ctx.lineWidth = 4;
        ctx.beginPath();
        ctx.arc(p.x + p.w/2, p.y + p.h/2 + p.floatOffset, p.w/2 + 8, 0, Math.PI * 2);
        ctx.stroke();
        
        ctx.strokeStyle = `rgba(78, 205, 196, ${shieldPulse * 0.5})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.arc(p.x + p.w/2, p.y + p.h/2 + p.floatOffset, p.w/2 + 12, 0, Math.PI * 2);
        ctx.stroke();
      }
    };

    const draw = () => {
      // Background is drawn unshaken so screen-shake never reveals gaps at the edges.
      drawBackground();

      const shaking = game.shakeFrames > 0;
      if (shaking) {
        const dx = (Math.random() * 2 - 1) * game.shakeMag;
        const dy = (Math.random() * 2 - 1) * game.shakeMag;
        ctx.save();
        ctx.translate(dx, dy);
        game.shakeFrames--;
        game.shakeMag *= 0.9;
      }

      drawAllPlatforms(ctx, game.platforms, imagesRef.current);
      drawAllCoins(ctx, game.coins, imagesRef.current?.coin);
      drawAllBoosts(ctx, game.boosts, imagesRef.current);
      drawAllProjectiles(ctx, game.projectiles, imagesRef.current?.projectile);
      drawAllEnemies(ctx, game.enemies, game.rocketActive || game.shieldActive, imagesRef.current?.spaceBear);
      drawParticles(ctx, game);
      drawPlayer();

      if (shaking) {
        ctx.restore();
      }
    };

    // Fixed-timestep loop: simulate at a constant 60Hz regardless of the
    // display refresh rate. This makes the run deterministic (a prerequisite for
    // server-side validation) and fixes a real fairness bug where the game ran
    // faster on high-refresh-rate monitors. Rendering still happens once per
    // animation frame.
    const STEP_MS = 1000 / 60;
    let acc = 0;
    let last = performance.now();

    const gameLoop = (now) => {
      if (!isRunning) return;
      const elapsed = Math.min(now - last, 250); // cap to avoid spiral of death
      last = now;
      if (!pausedRef.current) {
        acc += elapsed;
        let steps = 0;
        while (acc >= STEP_MS && steps < 5) {
          update();
          acc -= STEP_MS;
          steps++;
        }
        if (steps === 5) acc = 0; // dropped behind; resync instead of piling up
        draw();
      }
      animationFrameId = requestAnimationFrame(gameLoop);
    };

    resetGameState(game, cvs.width, cvs.height);
    // Dev mode is read by the simulation, so it must live on game state.
    game.devMode = devMode;
    if (devMode) {
      game.shootingEnabled = true;
      game.bulletsRemaining = 999;
    }

    initAudio();
    setAudioSettings({ sfx: game.options.soundEnabled });

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    cvs.addEventListener('touchstart', handleTouchStart, { passive: false });
    cvs.addEventListener('touchmove', handleTouchMove, { passive: false });
    cvs.addEventListener('touchend', handleTouchEnd);
    cvs.addEventListener('touchcancel', handleTouchCancel);
    last = performance.now();
    animationFrameId = requestAnimationFrame(gameLoop); // first tick gets a real timestamp

    // Auto-hide the control hint a few seconds in, even if the player stays still.
    const hintTimeout = setTimeout(() => setShowHint(false), 4500);

    return () => {
      isRunning = false;
      sfx.rocketStop();
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      clearTimeout(hintTimeout);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      cvs.removeEventListener('touchstart', handleTouchStart);
      cvs.removeEventListener('touchmove', handleTouchMove);
      cvs.removeEventListener('touchend', handleTouchEnd);
      cvs.removeEventListener('touchcancel', handleTouchCancel);
    };
  }, [gameOver, gameStarted, devMode, recordScore, togglePause]);

  const handleRestart = () => {
    const game = gameStateRef.current;
    const cvs = canvasRef.current;
    
    resetGameState(game, cvs.width, cvs.height);

    // Re-enable dev mode features after restart
    game.devMode = devMode;
    if (devMode) {
      game.shootingEnabled = true;
      game.bulletsRemaining = 999;
    }
    
    hudRef.current = {};
    pausedRef.current = false;
    setPaused(false);
    setShowHint(true); // surface the control hint at the start of each run
    setScore(0);
    setLiveScore(0);
    setFinalStats(null);
    setCoins(0);
    setCoinsTotal(0);
    setEnemiesKilled(0);
    setGameOver(false);
    setGameStarted(true);
    setActiveBoost(null);
    setBoostTimer(0);
    setAmmo(game.bulletsRemaining);
    setRocketProgress(0);
    setShieldSecs(0);
  };

  const handleStartGame = () => {
    setGameStarted(true);
    handleRestart();
  };

  const handleBackToMenu = () => {
    sfx.rocketStop();
    pausedRef.current = false;
    setPaused(false);
    setGameStarted(false);
    setGameOver(false);
    setShowOptions(false);
  };

  const toggleOption = (option) => {
    const game = gameStateRef.current;
    const newValue = !game.options[option];
    game.options[option] = newValue;

    game.keys = {};
    game.player.vx = 0;

    // Apply audio changes immediately so the toggle works mid-game.
    if (option === 'soundEnabled') {
      setAudioSettings({ sfx: newValue });
    }

    setOptions(prev => ({
      ...prev,
      [option]: newValue
    }));
  };

  return (
    <div className="game-container">
      <div className="game-card">
        {!gameStarted && <h1 className="game-title">Specky <i>to the Moon</i></h1>}

        {!gameStarted && !showOptions && !showHowToPlay && (
          <div className="menu-container">
            {tgUser && (
              <div className="tg-user">
                👋 {tgUser.username ? `@${tgUser.username}` : tgUser.first_name}
              </div>
            )}
            <div className="wallet-bar">
              {wallet.status === 'connected' && wallet.account ? (
                <div className="wallet-connected">
                  <div className="wallet-info">
                    <span className="wallet-addr" title={wallet.account.address}>
                      {shortAddr(wallet.account.address)}
                    </span>
                    <span className="wallet-bal">
                      {fmtAmount(wallet.account.balances.SOUL)} SOUL · {fmtAmount(wallet.account.balances.KCAL)} KCAL
                    </span>
                  </div>
                  <button className="wallet-btn" onClick={wallet.disconnect}>Disconnect</button>
                </div>
              ) : (
                <button
                  className="wallet-btn connect"
                  onClick={wallet.connect}
                  disabled={wallet.status === 'connecting'}
                >
                  {wallet.status === 'connecting' ? 'Connecting…' : '🔗 Connect Wallet'}
                </button>
              )}
              {wallet.status === 'error' && (
                <div className="wallet-error">{wallet.error}</div>
              )}
            </div>

            <div className="menu-icon">
              {imagesLoaded && imagesRef.current?.speckyLogo ? (
                <img
                  src="/images/specky-logo.png"
                  alt="Specky"
                  style={{width: '150px', height: '150px', margin: '0 auto'}}
                />
              ) : (
                <div className="menu-icon-emoji">🐻</div>
              )}
              <p className="menu-description">Jump, collect, dodge FUD and make your way to the Moon!</p>
            </div>
            
            <button onClick={handleStartGame} className="btn btn-primary">
              Practice
            </button>

            <button onClick={() => setShowHowToPlay(true)} className="btn btn-secondary">
              How to Play
            </button>
            
            <button onClick={() => setShowOptions(true)} className="btn btn-secondary">
              Options
            </button>
            
            {highScore > 0 && (
              <div className="high-score-box">
                <div className="high-score-label">Best Score</div>
                <div className="high-score-value">{highScore.toLocaleString()}</div>
              </div>
            )}
          </div>
        )}

        {showOptions && (
          <div className="options-container">
            <h2 className="options-title">Options</h2>
            
            <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              <div className="option-row">
                <span className="option-label">Enemies</span>
                <button
                  onClick={() => toggleOption('enemiesEnabled')}
                  className={`option-toggle ${options.enemiesEnabled ? 'on' : 'off'}`}
                >
                  {options.enemiesEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
              
              <div className="option-row">
                <span className="option-label">Power-ups</span>
                <button
                  onClick={() => toggleOption('boostsEnabled')}
                  className={`option-toggle ${options.boostsEnabled ? 'on' : 'off'}`}
                >
                  {options.boostsEnabled ? 'ON' : 'OFF'}
                </button>
              </div>
              
              <div className="option-row">
                <span className="option-label">Sound FX</span>
                <button
                  onClick={() => toggleOption('soundEnabled')}
                  className={`option-toggle ${options.soundEnabled ? 'on' : 'off'}`}
                >
                  {options.soundEnabled ? 'ON' : 'OFF'}
                </button>
              </div>

              {isTouchDevice && (
                <div className="option-row">
                  <span className="option-label">Tilt to Move</span>
                  <button
                    onClick={toggleTilt}
                    className={`option-toggle ${options.tiltEnabled ? 'on' : 'off'}`}
                  >
                    {options.tiltEnabled ? 'ON' : 'OFF'}
                  </button>
                </div>
              )}

              {tiltStatus && tiltStatus !== 'ok' && (
                <p className="wallet-error">
                  {tiltStatus === 'insecure'
                    ? 'Tilt needs a secure (HTTPS) connection — it works in the Telegram app or over an https link.'
                    : tiltStatus === 'denied'
                    ? 'Motion access was blocked. Allow it in your browser settings to use tilt.'
                    : "This device doesn't support tilt controls."}
                </p>
              )}

              <div className="option-row">
                <span className="option-label">Developer Mode</span>
                <button
                  onClick={() => setDevMode(!devMode)}
                  className={`option-toggle ${devMode ? 'on' : 'off'}`}
                >
                  {devMode ? 'ON' : 'OFF'}
                </button>
              </div>
            </div>
            
            <button onClick={() => setShowOptions(false)} className="btn btn-primary" style={{marginTop: '1.5rem'}}>
              Back to Menu
            </button>
            
            <div className="option-hints">
              <p>💡 Disable enemies for casual play</p>
              <p>💪 Disable power-ups for extra challenge</p>
            </div>
          </div>
        )}

        {showHowToPlay && (
  <div className="options-container">
    <h2 className="options-title">How to Play</h2>
    
    <div style={{textAlign: 'left', padding: '1rem', fontSize: '0.95rem', lineHeight: '1.6'}}>
      <h3 style={{color: 'var(--cyan)', marginTop: '1rem', marginBottom: '0.5rem'}}>Controls</h3>
      <p><strong>Desktop:</strong> Use ← → or A/D to move left and right</p>
      <p><strong>Mobile:</strong> Drag to move, tap upper screen to shoot</p>
      <p><strong>Shoot:</strong> Press ↑ or W (when you have ammo)</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Objective</h3>
      <p>Jump as high as possible! The game ends if you fall or hit an enemy.</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Platforms</h3>
<p><span style={{color: '#e2d453'}}>● Solid</span> - Normal platforms</p>
<p><span style={{color: '#2ca121'}}>● Moving</span> - Moves left and right</p>
<p><span style={{color: '#db6b21'}}>● Breaks</span> - Breaks after one jump</p>
<p><span style={{color: '#a8a741'}}>● Fragile</span> - Breaks on touch</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Coins</h3>
      <p>Collect coins by touching them or shooting them. Every 10 coins collected you unlock a rocket boost!</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Power-ups</h3>
      <p>🚀 <strong>Rocket</strong> - 3s fast climb + immunity (costs 10 coins)</p>
      <p>🛡️ <strong>Shield</strong> - 10s immunity from enemies</p>
      <p>🔫 <strong>Shooter</strong> - +10 bullets to destroy enemies</p>
      <p>💎 <strong>2x Coins</strong> - Double the coins collected for 8s</p>
      <p>🧲 <strong>Magnet</strong> - 5s, pulls nearby coins toward you</p>
      <p>🐌 <strong>Slow-mo</strong> - Slower movement for precision</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Enemies</h3>
      <p>Avoid the space bears (FUD)! Shoot them to destroy (some need multiple hits).</p>
      <p><strong>No enemies</strong> for the first 50 meters - practice your jumps!</p>
      
      <h3 style={{color: 'var(--cyan)', marginTop: '1.5rem', marginBottom: '0.5rem'}}>Difficulty</h3>
      <p>The higher you go, the harder it gets:</p>
      <p>• Fewer platforms</p>
      <p>• Smaller platforms (after 500m)</p>
      <p>• More enemies</p>
    </div>
    
    <button
      onClick={() => setShowHowToPlay(false)}
      className="btn btn-primary"
      style={{marginTop: '1.5rem'}}
    >
      Back to Menu
    </button>
  </div>
)}

        <div className={`play-area ${!gameStarted ? 'hidden' : ''}`}>
          <canvas
            ref={canvasRef}
            width={360}
            height={740}
            className="game-canvas"
          />

          {gameStarted && !gameOver && !paused && (
            <div className="hud-overlay">
              {devMode && <div className="dev-tag">🔧 DEV · ∞ AMMO</div>}

              <div className="hud-top">
                <div className="hud-score">
                  <span className="hud-score-label">SCORE</span>
                  <span className="hud-score-value" key={liveScore}>{liveScore.toLocaleString()}</span>
                </div>
                <button className="pause-btn" onClick={togglePause} aria-label="Pause">⏸</button>
              </div>

              <div className="hud-stats">
                <span className="hud-stat"><b>⛰</b>{score}m</span>
                <span
                  className="hud-stat"
                  style={{color: coinsTotal > 0 && coins === coinsTotal ? 'var(--green)' : 'var(--gold)'}}
                >
                  <b>🪙</b>{coins}/{coinsTotal}
                </span>
                <span
                  className="hud-stat"
                  style={{color: rocketProgress >= 10 ? 'var(--green)' : undefined}}
                >
                  <b>🚀</b>{rocketProgress}/10
                </span>
                <span
                  className="hud-stat"
                  style={{color: ammo > 0 ? 'var(--red)' : 'var(--text-mute)'}}
                >
                  <b>🔫</b>{ammo}
                </span>
              </div>

              {activeBoost && (
                <div className={`boost-banner ${
                  activeBoost === 'rocket' ? 'rocket' :
                  activeBoost === 'slowmo' ? 'slowmo' :
                  activeBoost === 'magnet' ? 'magnet' :
                  'other'
                }`}>
                  <div className="boost-content">
                    <span style={{fontSize: '1.1rem'}}>
                      {activeBoost === 'rocket' ? '🚀' :
                       activeBoost === 'slowmo' ? '🐌' :
                       activeBoost === 'magnet' ? '🧲' :
                       '💎'}
                    </span>
                    <span>
                      {activeBoost === 'rocket' ? 'ROCKET - IMMUNE!' :
                       activeBoost === 'slowmo' ? 'SLOW-MO' :
                       activeBoost === 'magnet' ? 'MAGNET' :
                       '2X COINS'} · {boostTimer}s
                    </span>
                  </div>
                </div>
              )}

              {shieldSecs > 0 && (
                <div className="shield-banner">
                  <div className="shield-content">
                    <span style={{fontSize: '1.1rem'}}>🛡️</span>
                    <span>SHIELD · {shieldSecs}s</span>
                  </div>
                </div>
              )}

              {showHint && (
                <div className="control-hint">
                  {isTouchDevice
                    ? (options.tiltEnabled
                        ? '📱 Tilt to move · hold to shoot'
                        : '👆 Drag to move · tap to shoot')
                    : '← →  move  ·  ↑ / W  shoot  ·  P  pause'}
                </div>
              )}
            </div>
          )}

        {paused && gameStarted && !gameOver && (
          <div className="game-over-overlay">
            <div className="game-over-card">
              <h2 className="game-over-title">PAUSED</h2>
              <div className="game-over-buttons">
                <button onClick={togglePause} className="btn-large btn-primary">
                  Resume
                </button>
                <button onClick={handleBackToMenu} className="btn-large btn-secondary">
                  Main Menu
                </button>
              </div>
            </div>
          </div>
        )}

        {gameOver && gameStarted && (() => {
          const fs = finalStats || {
            total: 0, height: score, coinsCollected: coins, coinsSpawned: coinsTotal,
            coinPct: 0, accuracy: 0, kills: enemiesKilled,
            heightScore: 0, coinScore: 0, combatScore: 0, perfectBonus: 0
          };
          const pct = Math.round(fs.coinPct * 100);
          const acc = Math.round(fs.accuracy * 100);
          const perfectCoins = fs.coinsSpawned > 0 && fs.coinsCollected >= fs.coinsSpawned;
          const isRecord = fs.total === highScore && fs.total > 0;
          const pts = (n) => <span className="score-points">+{n.toLocaleString()}</span>;
          return (
          <div className="game-over-overlay">
            <div className="game-over-card">
              <h2 className="game-over-title">GAME OVER</h2>

              <div className="total-score">
                <div className="total-score-label">TOTAL SCORE</div>
                <div className="total-score-value">{fs.total.toLocaleString()}</div>
                <div className="total-score-best">Best {highScore.toLocaleString()}</div>
              </div>

              {isRecord && (
                <div className="new-record">
                  <div className="new-record-text">🎉 NEW BEST SCORE! 🎉</div>
                </div>
              )}

              <div className="score-boxes">
                <div className="score-box height">
                  <div className="score-label">Height</div>
                  <div className="score-value height">{fs.height}m {pts(fs.heightScore)}</div>
                </div>

                <div className="score-box coins">
                  <div className="score-label">Coins {pct}%{perfectCoins ? ' 🏆' : ''}</div>
                  <div
                    className="score-value coins"
                    style={perfectCoins ? {color: 'var(--green)'} : undefined}
                  >
                    {fs.coinsCollected}/{fs.coinsSpawned} {pts(fs.coinScore + fs.perfectBonus)}
                  </div>
                </div>

                <div className="score-box enemies">
                  <div className="score-label">Enemies {fs.kills > 0 ? `· ${acc}% acc` : ''}</div>
                  <div className="score-value enemies">{fs.kills} {pts(fs.combatScore)}</div>
                </div>
              </div>

              <div className="game-over-buttons">
                <button onClick={handleRestart} className="btn-large btn-primary">
                  Play Again
                </button>

                <button onClick={handleBackToMenu} className="btn-large btn-secondary">
                  Main Menu
                </button>
              </div>
            </div>
          </div>
          );
        })()}
        </div>

      </div>


    </div>
  );
};

export default SpeckyJump;